import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ProjectAccessField, type ProjectAccessOption } from './ProjectAccessField';

export interface ProjectAccessUser {
  id: string;
  name: string;
  unique_id?: string;
  email?: string;
}

interface AssignedProject {
  id: string;
  name: string;
  status: string;
  client_name?: string;
}

interface ManageProjectAccessDialogProps {
  open: boolean;
  user: ProjectAccessUser | null;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

/**
 * Shared "Manage Project Access" dialog opened from the Users list. Loads the
 * active projects plus the user's current assignments, lets an admin manage the
 * assignment set (assigned cards + search + add + quick-create) and saves it
 * with the existing PUT /users/:id/projects contract. On failure the dialog
 * stays open with the selection preserved.
 */
export function ManageProjectAccessDialog({ open, user, onOpenChange, onSaved }: ManageProjectAccessDialogProps) {
  const { addToast } = useToast();
  const userId = user?.id ?? null;
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [saveError, setSaveError] = useState('');

  const activeProjectsQuery = useQuery({
    queryKey: ['admin-active-projects'],
    enabled: open && !!userId,
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: { id: string; name: string; client_name?: string }[] }>('/projects?availability=active&limit=1000');
      return r.data ?? [];
    },
  });

  const userProjectsQuery = useQuery({
    queryKey: ['admin-user-projects', userId ?? null],
    enabled: open && !!userId,
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: AssignedProject[] }>(`/users/${userId}/projects`);
      return r.data ?? [];
    },
  });

  // Active projects plus any assigned project that is no longer active (kept so
  // existing assignments stay selectable and visible in the list).
  const projectOptions: ProjectAccessOption[] = useMemo(() => {
    const map = new Map<string, ProjectAccessOption>();
    for (const p of activeProjectsQuery.data ?? []) {
      map.set(p.id, { value: p.id, label: p.name, sublabel: p.client_name || undefined });
    }
    for (const a of userProjectsQuery.data ?? []) {
      if (!map.has(a.id)) {
        map.set(a.id, {
          value: a.id,
          label: a.status === 'active' ? a.name : `${a.name} (${a.status})`,
          sublabel: a.client_name || undefined,
        });
      }
    }
    return [...map.values()];
  }, [activeProjectsQuery.data, userProjectsQuery.data]);

  useEffect(() => {
    if (open && userId) {
      setSaveError('');
      setSelectedProjectIds((userProjectsQuery.data ?? []).map((p) => p.id));
    }
  }, [open, userId, userProjectsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/users/${userId}/projects`, { projectIds: selectedProjectIds }),
    onSuccess: () => {
      addToast('Project assignments saved.', 'success');
      onSaved?.();
      onOpenChange(false);
    },
    onError: (err: any) => {
      const msg = err.message || 'Unable to save project assignments.';
      addToast(msg, 'error');
      setSaveError(msg);
    },
  });

  const selectedCount = selectedProjectIds.length;
  const countLabel = selectedCount === 0 ? 'No projects selected' : selectedCount === 1 ? '1 project selected' : `${selectedCount} projects selected`;

  const isLoading = activeProjectsQuery.isLoading || userProjectsQuery.isLoading;
  const isError = activeProjectsQuery.isError || userProjectsQuery.isError;
  const onRetry = () => {
    activeProjectsQuery.refetch();
    userProjectsQuery.refetch();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setSaveError('');
          onOpenChange(false);
        }
      }}
    >
      <DialogContent className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-4 pb-4 pt-4 sm:px-6 sm:pt-6">
          <DialogTitle>Manage Project Access</DialogTitle>
          <DialogDescription>
            Choose which active projects {user?.name} ({user?.unique_id || user?.email}) can access. Users only see
            data for the projects they are assigned to.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          <p className="text-sm font-medium text-primary">Projects this user can access</p>
          <ProjectAccessField
            options={projectOptions}
            value={selectedProjectIds}
            onValueChange={setSelectedProjectIds}
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
            onProjectCreated={() => activeProjectsQuery.refetch()}
          />
          {saveError && <p className="text-sm text-error">{saveError}</p>}
        </div>
        <DialogFooter className="shrink-0 flex-col items-stretch gap-3 border-t bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:space-x-0 sm:py-4">
          <p className="text-xs text-muted">{countLabel}</p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" className="min-h-[44px]" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px]"
              disabled={saveMutation.isPending}
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save Access
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}